import React from 'react'
import styled from 'styled-components'
import Modal, { CloseButton } from '@jarvis.oddle/core/components/Modal'
import { H4, LinkText } from '@jarvis.oddle/core/components/Typo'
import Button from '@jarvis.oddle/core/components/Button'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { RadioGroup } from '@jarvis.oddle/core/components/Radio'
import CreditCardItem from 'src/components/molecules/CreditCardItem'
import {
  margin,
  marginSizes,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import { isObjectEmpty } from 'src/utils/object'
import Divider from '@jarvis.oddle/core/components/Divider'
import Flex from '@jarvis.oddle/core/components/Flex'
import showNotificationToast from '@jarvis.oddle/core/components/ToastNotification'
import useAppSelector from 'src/hooks/useAppSelector'
import { getAllSavedCardsAction, selectUserSavedCards } from 'src/redux/user'
import { selectSavedCreditCardAction } from 'src/redux/checkout'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import modalController, {
  makeControlledModal
} from 'src/decorators/ModalController'

const StyledModalSection = styled(Modal.Section)`
  && {
    display: flex;
    flex-grow: 1;
    flex-shrink: 0;
  }
`

const StyledCreditCardItem = styled(CreditCardItem)`
  margin-bottom: ${margin[marginSizes.small]};

  &:hover {
    opacity: 0.8;
  }
`

const CreditCardChooserModal = ({
  modal,
  selectedSavedCreditCard,
  onSelect
}: {
  modal: {
    show: boolean
    onClose: () => void
    onClosed: () => void
  }
  selectedSavedCreditCard: Oms.V4.SelectedSavedCreditCard
  onSelect?: () => void
}) => {
  const { t } = React.useContext(TranslationContext)
  const savedCardsState = useAppSelector(selectUserSavedCards)
  const dispatch = useAppDispatch()
  const savedCards = React.useMemo(
    () => savedCardsState.data.paymentMethods || [],
    [savedCardsState.data.paymentMethods]
  )
  const defaultSavedCreditCard = React.useMemo(
    () => savedCardsState.data.defaultPmId || '',
    [savedCardsState.data.defaultPmId]
  )
  const [selectedCreditCard, setSelectedCreditCard] = React.useState(
    {} as Oms.V4.SelectedSavedCreditCard
  )
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    if (!isObjectEmpty(selectedSavedCreditCard)) {
      setSelectedCreditCard(selectedSavedCreditCard)
    } else if (savedCards.length > 0) {
      const defaultCard = savedCards.find(
        (creditCard) => creditCard.id === defaultSavedCreditCard
      )
      if (defaultCard) {
        setSelectedCreditCard(defaultCard)
      } else {
        setSelectedCreditCard(savedCards[0])
      }
    }
  }, [defaultSavedCreditCard, savedCards, selectedSavedCreditCard])

  const onAddCreditCard = async (response) => {
    setLoading(true)
    if (response.success) {
      const listSavedCard = await dispatch(getAllSavedCardsAction())
      if (
        listSavedCard.meta.requestStatus === 'fulfilled' &&
        listSavedCard.payload?.paymentMethods?.length > 0
      ) {
        dispatch(
          selectSavedCreditCardAction(
            listSavedCard.payload.paymentMethods[0]
          )
        )
      }
      showNotificationToast.success(t('Credit card successfully added.'))
    } else {
      showNotificationToast.error(
        t('Oops! There seems to be a problem. Please try again later.')
      )
    }
    setLoading(false)
  }

  const onClickAddNewCard = () => {
    modalController.showModal('CreditCardAdditionalModal', {
      onSaveCard: (creditCard) => onAddCreditCard(creditCard)
    })
  }

  const handleSubmit = () => {
    dispatch(selectSavedCreditCardAction(selectedCreditCard))
    if (onSelect) {
      onSelect()
    }
    modal.onClose()
  }

  return (
    <Modal {...modal}>
      <Modal.FlexContainer>
        <Modal.Header>
          <H4 margin='none' bold>
            {t('Manage Cards')}
          </H4>
          <CloseButton onClick={modal.onClose} />
        </Modal.Header>
        <Modal.Body>
          <StyledModalSection>
            <RadioGroup
              name='chooseSavedCreditCard'
              selected={selectedCreditCard.id}
              controlled
            >
              {savedCards.map((creditCard) => (
                <StyledCreditCardItem
                  key={creditCard.id}
                  creditCard={creditCard}
                  defaultCreditCard={creditCard.id === defaultSavedCreditCard}
                  onClickRadio={() => setSelectedCreditCard(creditCard)}
                  loading={loading}
                />
              ))}
            </RadioGroup>
            {savedCards.length > 0 && <Divider />}
            <Flex justifyContent='center' padding={paddingSizes.medium}>
              <LinkText onClick={onClickAddNewCard} heading>
                {t('Add new Card')}
              </LinkText>
            </Flex>
          </StyledModalSection>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type='button'
            fullWidth
            onClick={handleSubmit}
            disabled={isObjectEmpty(selectedCreditCard) || loading}
            loading={loading}
          >
            {t('Select this Card')}
          </Button>
        </Modal.Footer>
      </Modal.FlexContainer>
    </Modal>
  )
}

export default makeControlledModal(
  CreditCardChooserModal,
  'CreditCardChooserModal'
)
